/**
 * NervousSystemVisualEngine — Branching calming currents throughout the body
 *
 * Visual: Warm green-gold branching light paths flowing through full body
 * Very faint full-body outline (5-8% opacity, heavily blurred)
 * Feel: settling, calming, connected, whole
 *
 * Color: Warm green → gold → soft emerald
 * Position: full vertical span (center)
 * Movement: downward flowing currents along branching paths, settling
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const NervousSystemVisualEngine = ({ elapsed = 0, totalDuration = 420, isActive = false }) => {
  const [breath, setBreath] = useState(0.5);
  const [pulse, setPulse] = useState(0.5);
  const [flowPhase, setFlowPhase] = useState(0);
  const [depthDrift, setDepthDrift] = useState(1);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    startRef.current = Date.now();
    const tick = () => {
      const now = Date.now() - startRef.current;
      setBreath(0.5 + 0.5 * Math.sin((now % 14000) / 14000 * Math.PI * 2 - Math.PI / 2));
      setPulse(0.5 + 0.5 * Math.sin((now % 10000) / 10000 * Math.PI * 2 - Math.PI / 2));
      setFlowPhase((now % 6000) / 6000);
      setDepthDrift(1 + 0.01 * Math.sin((now % 22000) / 22000 * Math.PI * 2));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  const phase = useMemo(() => {
    if (elapsed < 60) return 'arrival';
    if (elapsed < 180) return 'expansion';
    if (elapsed < 300) return 'settling';
    return 'integration';
  }, [elapsed]);

  const intensity = useMemo(() => {
    if (phase === 'arrival') return (0.3 + Math.min(1, elapsed / 60) * 0.3) + breath * 0.08;
    if (phase === 'expansion') return (0.6 + ((elapsed - 60) / 120) * 0.25) + breath * 0.1;
    if (phase === 'settling') return 0.88 + breath * 0.06;
    return 0.9 + breath * 0.04;
  }, [phase, elapsed, breath]);

  const expansion = useMemo(() => {
    if (phase === 'arrival') return 0.3 + Math.min(1, elapsed / 60) * 0.2;
    if (phase === 'expansion') return 0.5 + ((elapsed - 60) / 120) * 0.3;
    if (phase === 'settling') return 0.8 + ((elapsed - 180) / 120) * 0.1;
    return 0.9;
  }, [phase, elapsed]);

  // Branching path nodes — key points along the nervous system
  const branchNodes = useMemo(() => [
    { x: 50, y: 12, size: 1.0, label: 'crown' },
    { x: 50, y: 22, size: 0.8, label: 'brain' },
    { x: 50, y: 32, size: 0.6, label: 'throat' },
    { x: 42, y: 40, size: 0.5, label: 'left_shoulder' },
    { x: 58, y: 40, size: 0.5, label: 'right_shoulder' },
    { x: 50, y: 42, size: 0.7, label: 'heart' },
    { x: 35, y: 52, size: 0.4, label: 'left_arm' },
    { x: 65, y: 52, size: 0.4, label: 'right_arm' },
    { x: 50, y: 55, size: 0.6, label: 'solar' },
    { x: 50, y: 65, size: 0.5, label: 'sacral' },
    { x: 50, y: 75, size: 0.5, label: 'root' },
    { x: 42, y: 85, size: 0.35, label: 'left_leg' },
    { x: 58, y: 85, size: 0.35, label: 'right_leg' },
  ], []);

  // Flow offset for each node — creates downward cascading movement
  const getFlowGlow = (nodeIdx) => {
    const offset = (flowPhase + nodeIdx * 0.08) % 1;
    return 0.3 + 0.7 * Math.sin(offset * Math.PI);
  };

  return (
    <motion.div className="absolute inset-0 overflow-hidden" data-testid="nervous-system-visual-engine"
      style={{ transform: `scale(${depthDrift})`, transformOrigin: '50% 45%' }}>

      {/* Deep warm-green darkness */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 100% 95% at 50% 45%, rgba(18,22,14,${0.5 + intensity * 0.3}) 0%, rgba(8,10,5,1) 60%)`,
      }} />

      {/* Whole-body atmosphere */}
      <motion.div className="absolute inset-0 pointer-events-none"
        animate={{ opacity: 0.03 + breath * 0.04 + intensity * 0.03 }}
        transition={{ duration: 3, ease: 'easeInOut' }}
        style={{ background: `radial-gradient(ellipse 50% 80% at 50% 45%, rgba(170,195,140,0.1) 0%, transparent 55%)` }} />

      {/* Very faint full-body outline */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.svg viewBox="0 0 200 700" style={{ width: '180px', height: '75vh', filter: 'blur(20px)' }}
          animate={{ opacity: 0.045 + breath * 0.015 }} transition={{ duration: 3 }}>
          <path d={`
            M 100,20 C 115,20 124,32 124,44 C 124,52 118,58 112,62
            C 130,68 165,85 178,112 C 186,130 182,150 175,168
            C 170,200 166,250 164,310 C 162,370 162,420 164,470
            C 166,510 170,540 176,575 L 24,575
            C 30,540 34,510 36,470 C 38,420 38,370 36,310
            C 34,250 30,200 25,168 C 18,150 14,130 22,112
            C 35,85 70,68 88,62 C 82,58 76,52 76,44
            C 76,32 85,20 100,20 Z
          `} fill="none" stroke="rgba(150,180,120,0.06)" strokeWidth="1.5" />
        </motion.svg>
      </div>

      {/* Branching nervous system paths — SVG connecting lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
        <defs>
          <filter id="ns-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Central spine line */}
        <motion.line x1="50%" y1="12%" x2="50%" y2="75%"
          stroke={`rgba(180,200,140,${0.06 + intensity * 0.08})`}
          strokeWidth="1.5" filter="url(#ns-glow)"
          animate={{ opacity: 0.04 + intensity * 0.08 }} />
        {/* Shoulder branches */}
        <motion.line x1="50%" y1="32%" x2="35%" y2="52%"
          stroke={`rgba(180,200,140,${0.04 + intensity * 0.06})`}
          strokeWidth="1" filter="url(#ns-glow)"
          animate={{ opacity: 0.03 + intensity * 0.06 }} />
        <motion.line x1="50%" y1="32%" x2="65%" y2="52%"
          stroke={`rgba(180,200,140,${0.04 + intensity * 0.06})`}
          strokeWidth="1" filter="url(#ns-glow)"
          animate={{ opacity: 0.03 + intensity * 0.06 }} />
        {/* Leg branches */}
        <motion.line x1="50%" y1="75%" x2="42%" y2="90%"
          stroke={`rgba(180,200,140,${0.03 + intensity * 0.05})`}
          strokeWidth="1" filter="url(#ns-glow)"
          animate={{ opacity: 0.03 + intensity * 0.05 }} />
        <motion.line x1="50%" y1="75%" x2="58%" y2="90%"
          stroke={`rgba(180,200,140,${0.03 + intensity * 0.05})`}
          strokeWidth="1" filter="url(#ns-glow)"
          animate={{ opacity: 0.03 + intensity * 0.05 }} />
      </svg>

      {/* Branch node glows — flowing downward */}
      {branchNodes.map((node, i) => {
        const glow = getFlowGlow(i);
        const nodeSize = 14 + expansion * 22 * node.size + pulse * 8 * node.size;
        return (
          <motion.div key={node.label} className="absolute pointer-events-none rounded-full"
            style={{ left: `${node.x}%`, top: `${node.y}%`, transform: 'translate(-50%, -50%)' }}
            animate={{
              width: nodeSize + breath * 4,
              height: nodeSize + breath * 3,
              opacity: (0.08 + intensity * 0.18 + glow * 0.12) * node.size,
            }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}>
            <div className="w-full h-full rounded-full" style={{
              background: `radial-gradient(circle, rgba(195,215,155,${0.5 * glow}) 0%, rgba(180,200,140,${0.15 * glow}) 40%, transparent 65%)`,
              filter: 'blur(4px)',
            }} />
          </motion.div>
        );
      })}

      {/* Central spine glow */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: '50%', top: '45%', transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 30 + expansion * 25 + breath * 6,
          height: 250 + expansion * 150 + breath * 20,
          opacity: 0.06 + intensity * 0.12 + pulse * 0.03,
        }}
        transition={{ duration: 2.5, ease: 'easeInOut' }}>
        <div className="w-full h-full" style={{
          background: 'linear-gradient(180deg, rgba(195,215,155,0.15) 0%, rgba(185,205,145,0.08) 30%, rgba(175,195,135,0.12) 60%, rgba(165,185,125,0.04) 100%)',
          filter: 'blur(12px)',
          borderRadius: '50%',
        }} />
      </motion.div>

      {/* CONNECTION LAYER — calm extends beyond body, filling entire space */}
      {(phase === 'settling' || phase === 'integration') && (
        <motion.div className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{
            opacity: phase === 'settling'
              ? Math.min(0.25, ((elapsed - 180) / 120) * 0.25) * (0.85 + breath * 0.15)
              : 0.25 + Math.min(0.2, ((elapsed - 300) / 120) * 0.2) * (0.85 + breath * 0.15),
          }}
          transition={{ duration: 6, ease: 'easeInOut' }}>
          <div className="absolute inset-0" style={{
            background: `radial-gradient(ellipse 85% 90% at 50% 45%, rgba(195,215,155,0.14) 0%, rgba(180,200,140,0.05) 30%, rgba(165,185,125,0.02) 50%, transparent 65%)`,
          }} />
        </motion.div>
      )}

      {/* Integration — whole body calm */}
      {phase === 'integration' && (
        <motion.div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse 45% 70% at 50% 45%, rgba(190,210,150,0.15) 0%, transparent 50%)` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: Math.min(0.4, ((elapsed - 300) / 120) * 0.4) * (0.8 + breath * 0.2) }}
          transition={{ duration: 5 }} />
      )}

      {/* Green-gold particles */}
      {[...Array(10)].map((_, i) => (
        <motion.div key={`p-${i}`} className="absolute rounded-full pointer-events-none"
          style={{ width: 2 + (i % 3) * 0.5, height: 2 + (i % 3) * 0.5, background: `rgba(185,205,145,${0.06 + (i % 3) * 0.03})`,
            left: `${25 + (i * 6) % 50}%`, top: `${15 + (i * 7) % 60}%` }}
          animate={{ y: [0, 8 + (i % 3) * 3, 0], x: [0, -2 + (i % 2) * 3, 0], opacity: [0, 0.08 + intensity * 0.05, 0] }}
          transition={{ duration: 14 + (i % 4) * 3, repeat: Infinity, delay: i * 1.5, ease: 'easeInOut' }} />
      ))}
    </motion.div>
  );
};

export default NervousSystemVisualEngine;
